import { SseParser } from "./sse.js";
import type { AuthenticateRequest, Chat, PostMessageRequest } from "./types.js";

export class ApiClient {
	#baseUrl = "/api";

	#fetch = (
		path: `/${string}`,
		options: RequestInit = {},
		throwOnNotOk = true,
	): Promise<Response> => {
		options = {
			...options,
			headers: { ...options.headers, Accepts: "application/json" },
			credentials: "include",
		};
		if (options.method === "POST") {
			options.headers = {
				...options.headers,
				"Content-Type": "application/json",
			};
		}
		return fetch(`${this.#baseUrl}${path}`, options).then((res) => {
			if (throwOnNotOk && !res.ok) {
				throw new Error(`Bad response code: ${res.status}`);
			}
			return res;
		});
	};

	async checkIfAuthenticated(): Promise<boolean> {
		// TODO: validate response
		return (await this.#fetch("/auth/").then((res) =>
			res.json(),
		)) as boolean;
	}

	async authenticate({ password }: AuthenticateRequest): Promise<void> {
		const res = await this.#fetch(
			"/auth/login",
			{
				method: "POST",
				body: JSON.stringify({ password }),
			},
			false,
		);
		if (res.status === 401) {
			throw new Error("Invalid credentials.");
		}
		if (res.status === 429) {
			const retryAfter = res.headers.get("Retry-After");
			throw new Error(
				`Too many attempts, try again ${retryAfter != null ? `after ${retryAfter}s` : "later"}.`,
			);
		}
	}

	async getChat(chatId: string): Promise<Chat> {
		return this.#fetch(`/chat/${chatId}`).then((res) => res.json());
	}

	async *postMessage(
		chatId: string,
		req: PostMessageRequest,
	): AsyncGenerator<string> {
		const res = await this.#fetch(`/chat/${chatId}`, {
			method: "POST",
			body: JSON.stringify(req),
		});
		if (!res.body) {
			return;
		}

		const parser = new SseParser(res);
		for await (const message of parser.parse()) {
			switch (message.type) {
				case "message": {
					yield String(message.data) || "\n";
				}
			}
		}
	}
}
